import { SOCIAL_MARKS } from "@tripcart/shared";

/**
 * Review-platform brand marks — Google, Trustpilot, TripAdvisor, Facebook.
 *
 * Third-party brand assets. **The fills are SVG attributes, not theme roles,
 * and must stay that way**: recoloured to a tenant's brand these stop being
 * marks, and Google's brand guidelines do not permit it either.
 */
function GoogleLogo({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

/**
 * The official marks below (Trustpilot, Tripadvisor, Facebook) are the paths
 * from Simple Icons 13.21.0 (CC0), which traces each brand's own asset, with
 * the brand's own colour: Trustpilot #00B67A, Tripadvisor #34E0A1 (its owl in
 * black on a green disc, as Tripadvisor draws its icon), Facebook #0866FF.
 * Replaced hand-drawn look-alikes (TC-44 #16).
 */
function TrustpilotLogo({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#00B67A"
      aria-hidden
    >
      <path d="M17.227 16.67l2.19 6.742-7.413-5.388 5.223-1.354zM24 9.31h-9.165L12.005.589l-2.84 8.723L0 9.3l7.422 5.397-2.84 8.714 7.422-5.388 4.583-3.326L24 9.311z" />
    </svg>
  );
}

function TripadvisorLogo({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="12" fill="#34E0A1" />
      <path
        transform="translate(4 4) scale(0.6667)"
        fill="#000000"
        d="M12.006 4.295c-2.67 0-5.338.784-7.645 2.353H0l1.963 2.135a5.997 5.997 0 0 0 4.04 10.43 5.976 5.976 0 0 0 4.075-1.6L12 19.705l1.922-2.09a5.972 5.972 0 0 0 4.072 1.598 6 6 0 0 0 6-5.998 5.982 5.982 0 0 0-1.957-4.432L24 6.648h-4.35a13.573 13.573 0 0 0-7.644-2.353zM12 6.255c1.531 0 3.063.303 4.504.903C13.943 8.138 12 10.43 12 13.1c0-2.671-1.942-4.962-4.504-5.942A11.72 11.72 0 0 1 12 6.256zM6.002 9.157a4.059 4.059 0 1 1 0 8.118 4.059 4.059 0 0 1 0-8.118zm11.992.002a4.057 4.057 0 1 1 .003 8.115 4.057 4.057 0 0 1-.003-8.115zm-11.992 1.93a2.128 2.128 0 0 0 0 4.256 2.128 2.128 0 0 0 0-4.256zm11.992 0a2.128 2.128 0 0 0 0 4.256 2.128 2.128 0 0 0 0-4.256z"
      />
    </svg>
  );
}

function FacebookLogo({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="#0866FF"
      aria-hidden
    >
      <path d={SOCIAL_MARKS.facebook?.path} />
    </svg>
  );
}

export function PlatformLogo({
  platform,
  size = 16,
}: {
  platform: string;
  size?: number;
}): React.ReactElement | null {
  switch (platform) {
    case "google":
      return <GoogleLogo size={size} />;
    case "trustpilot":
      return <TrustpilotLogo size={size} />;
    case "tripadvisor":
      return <TripadvisorLogo size={size} />;
    case "facebook":
      return <FacebookLogo size={size} />;
    default:
      return null;
  }
}
